import { useEffect, useRef, useState } from "react";
import { PLANNING_STATES } from "../classes/enums/PlanningStateEnum";
import {
  hasCorrectCheckViews,
  organizeSurgeryResultsByModality,
  shouldRefreshCheckViews,
} from "../classes/utils/CheckViewUtils";

/**
 * Custom hook to manage surgery data loading for Check module
 * Handles cache validation, reload tokens, and view loading
 * @param {Object} params - Hook parameters
 * @returns {Object} Loading state and functions
 */
const useCheckSurgeryLoader = ({
  constructedImageData,
  renderClient,
  allInstances,
  instances,
  digitalTwinSurgeryCache,
  twinId,
  remoteKey,
  checkReloadToken,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [areViewsReady, setAreViewsReady] = useState(false);
  const lastHandledReloadTokenRef = useRef(0);

  useEffect(() => {
    const hasExternalReloadRequest =
      checkReloadToken !== lastHandledReloadTokenRef.current &&
      checkReloadToken !== 0;

    if (hasExternalReloadRequest) {
      lastHandledReloadTokenRef.current = checkReloadToken;
    }

    const shouldRefreshData = shouldRefreshCheckViews(
      allInstances,
      twinId,
      digitalTwinSurgeryCache
    );

    const hasCorrectViews = hasCorrectCheckViews(
      allInstances,
      twinId,
      digitalTwinSurgeryCache
    );

    const forceReloadFlag = shouldRefreshData || hasExternalReloadRequest;

    const shouldLoadSurgery =
      constructedImageData &&
      renderClient &&
      allInstances.length > 0 &&
      instances &&
      (!hasCorrectViews || forceReloadFlag);

    if (shouldLoadSurgery) {
      setIsLoading(true);

      (async () => {
        try {
          const response = await renderClient.LoadSpinesimViewStage(
            remoteKey,
            JSON.stringify(constructedImageData),
            PLANNING_STATES.CHECK,
            forceReloadFlag
          );

          const surgeryResult = JSON.parse(response);
          surgeryResult.forEach((view, index) => {});

          organizeSurgeryResultsByModality(
            surgeryResult,
            instances,
            allInstances,
            twinId,
            digitalTwinSurgeryCache
          );

          setAreViewsReady(true);
        } catch (error) {
        } finally {
          setIsLoading(false);
        }
      })();
    } else if (!shouldRefreshData && hasCorrectViews) {
      setAreViewsReady(true);
    } else {
    }
  }, [
    constructedImageData,
    renderClient,
    allInstances,
    instances,
    digitalTwinSurgeryCache,
    twinId,
    remoteKey,
    checkReloadToken,
  ]);

  return {
    isLoading,
    areViewsReady,
    setAreViewsReady,
  };
};

export default useCheckSurgeryLoader;
